import React from 'react';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import { Settings, Wrench, Truck, HeadphonesIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import { useLanguage } from '../../i18n/LanguageContext';
import { services as servicesData } from '../../data/content';

const iconMap: Record<string, any> = { Settings, Wrench, Truck, HeadphonesIcon };

const Services = () => {
  const { t, pick, path } = useLanguage();

  const services = servicesData.map((s: any) => ({ ...s, icon: iconMap[s.icon] }));

  const pageStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #2d2d2d 100%)',
    paddingTop: '4rem'
  };

  const containerStyle = {
    maxWidth: '72rem',
    margin: '0 auto',
    padding: '2rem 1.5rem'
  };

  const headerStyle = {
    textAlign: 'center' as const,
    marginBottom: '4rem'
  };

  const titleStyle = {
    fontSize: '3rem',
    fontWeight: 'bold',
    color: 'white',
    marginBottom: '1rem'
  };

  const highlightStyle = {
    color: '#dfa926'
  };

  const descriptionStyle = {
    fontSize: '1.25rem',
    color: '#d1d5db',
    maxWidth: '48rem',
    margin: '0 auto',
    lineHeight: '1.6'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '2rem',
    marginBottom: '4rem'
  };

  const cardStyle = {
    background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.5) 0%, rgba(17, 17, 17, 0.5) 100%)',
    padding: '2rem',
    borderRadius: '0.75rem',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(58, 58, 58, 0.5)',
    transition: 'all 0.3s ease'
  };

  const iconStyle = {
    width: '3rem',
    height: '3rem',
    color: '#dfa926',
    marginBottom: '1.5rem'
  };

  const cardTitleStyle = {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: 'white',
    marginBottom: '1rem'
  };

  const cardDescStyle = {
    color: '#9ca3af',
    marginBottom: '1.5rem',
    lineHeight: '1.6'
  };

  const featureListStyle = {
    listStyle: 'none',
    padding: 0,
    margin: 0
  };

  const featureItemStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '0.5rem',
    color: '#d1d5db',
    fontSize: '0.875rem'
  };

  const bulletStyle = {
    width: '0.5rem',
    height: '0.5rem',
    backgroundColor: '#dfa926',
    borderRadius: '50%',
    marginRight: '0.75rem'
  };

  return (
    <div style={pageStyle}>
      <SEO
        title={t('seo.services.title')}
        description={t('seo.services.description')}
        canonicalPath="/services"
        keywords={t('seo.services.keywords')}
      />
      <Navigation />
      
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>
            {t('services.titlePre')} <span style={highlightStyle}>{t('services.titleHl')}</span>
          </h1>
          <p style={descriptionStyle}>
            {t('services.desc')}
          </p>
        </div>

        <div style={gridStyle}>
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <div 
                key={index}
                style={cardStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(223, 169, 38, 0.5)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(58, 58, 58, 0.5)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <IconComponent style={iconStyle} />
                <h3 style={cardTitleStyle}>{pick(service, 'title')}</h3>
                <p style={cardDescStyle}>{pick(service, 'description')}</p>

                <ul style={featureListStyle}>
                  {pick(service, 'features').map((feature: string, featureIndex: number) => (
                    <li key={featureIndex} style={featureItemStyle}>
                      <span style={bulletStyle}></span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div style={{ textAlign: 'center', marginTop: '3rem' }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(223, 169, 38, 0.1) 0%, rgba(223, 169, 38, 0.05) 100%)',
            padding: '3rem',
            borderRadius: '0.75rem',
            border: '1px solid rgba(223, 169, 38, 0.2)'
          }}>
            <h3 style={{ color: 'white', fontSize: '2rem', marginBottom: '1rem' }}>
              {t('services.ctaTitle')}
            </h3>
            <p style={{ color: '#d1d5db', marginBottom: '2rem', fontSize: '1.125rem' }}>
              {t('services.ctaDesc')}
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link
                to={path('/contact')}
                style={{
                  backgroundColor: '#dfa926',
                  color: 'black',
                  padding: '0.75rem 2rem',
                  borderRadius: '0.5rem',
                  textDecoration: 'none',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  display: 'inline-block'
                }}
                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#c8941f'}
                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#dfa926'}
              >
                {t('common.quote')}
              </Link>
              <Link
                to={path('/products')}
                style={{
                  border: '2px solid #dfa926',
                  color: '#dfa926',
                  backgroundColor: 'transparent',
                  padding: '0.75rem 2rem',
                  borderRadius: '0.5rem',
                  textDecoration: 'none',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  display: 'inline-block'
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.backgroundColor = '#dfa926';
                  (e.target as HTMLElement).style.color = 'black';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.backgroundColor = 'transparent';
                  (e.target as HTMLElement).style.color = '#dfa926';
                }}
              >
                {t('services.viewProducts')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Services;
